import { isDefined } from '@sgme/fp';
import { useContext } from 'react';

import { useTrackEvent } from '@/App/Blotter/useTrackEvent.ts';
import { OrderRow } from '@/App/tiles/orders/components/OrderRow.tsx';
import {
  TakeProfitContext,
  useValidateTakeProfit,
} from '@/App/tiles/orders/TakeProfitOrder/TakeProfitOrderCreationTile/takeProfitContext.tsx';
import { SgNumberInput } from '@/components/sg-design-system/components/input/SgNumberInput';
import { OmsFieldErrorWrapper } from '@/components/ui/OmsFieldErrorWrapper.tsx';
import { useAppSelector } from '@/store/hooks.ts';
import {
  selectOrderEntryByIdFromBlotter,
  selectTakeProfitErrors,
  selectTradeIdFromTakeProfitTile,
} from '@/store/orders/takeProfitOrder/selectors.ts';
import { useDebounce } from '@/utils/hooks/debounce.ts';

type TakeProfitCommercialMarginProps = {
  commercialMargin: number | undefined;
  setCommercialMargin: (value: number | undefined) => void;
  setLimitPrice: (value: number | undefined) => void;
  setCustomerPrice: (value: number | undefined) => void;
};

export const TakeProfitEditingCommercialMargin = (props: TakeProfitCommercialMarginProps) => {
  const { commercialMargin, setCommercialMargin, setCustomerPrice, setLimitPrice } = props;
  const { tileId, setIsModifyingField } = useContext(TakeProfitContext);
  const track = useTrackEvent();

  const tradeId = useAppSelector((state) => selectTradeIdFromTakeProfitTile(state, tileId));
  const orderFromBlotter = useAppSelector((state) =>
    tradeId ? selectOrderEntryByIdFromBlotter(state, tradeId) : undefined,
  );

  const errors = useAppSelector((state) => selectTakeProfitErrors(state, tileId));
  const commercialMarginError = errors?.commercialMargin;

  const validateTakeProfit = useValidateTakeProfit();

  const handleCommercialMarginChange = useDebounce(async (value: number | undefined) => {
    track('Order action', 'Commercial margin changed');
    if (isDefined(orderFromBlotter)) {
      const response = await validateTakeProfit({
        commercialMargin: value,
      });

      if (response?.data?.validationResponseDetailContent.length === 0) {
        setCommercialMargin(value);
        isDefined(response?.data?.limitPrice) && setLimitPrice(response?.data?.limitPrice);
        isDefined(response?.data?.customerPrice) && setCustomerPrice(response?.data?.customerPrice);
      }
    }
  }, 500);

  return (
    <OrderRow labelId="order.fields.commercialMargin.label">
      <OmsFieldErrorWrapper errors={commercialMarginError}>
        <SgNumberInput
          valuePosition="start"
          selectAllOnFocus
          maxDecimals={9}
          value={
            commercialMargin ?? (isDefined(orderFromBlotter?.margin) ? (orderFromBlotter?.margin as number) : undefined)
          }
          onChange={handleCommercialMarginChange}
          onFocus={() => setIsModifyingField(true)}
          onBlur={() => setIsModifyingField(false)}
          data-e2e="commercial-margin"
          size="sm"
          state={isDefined(commercialMarginError) ? 'invalid' : undefined}
        />
      </OmsFieldErrorWrapper>
    </OrderRow>
  );
};
